import React, { useState, useRef, useCallback } from 'react'
import InstagramCropper from './InstagramCropper'
import AppBar from './AppBar'
import { devDebug } from '../utils/devDebug'

/** iOS WKWebView 등에서 초대형 캔버스면 toDataURL이 비는 경우가 있어 상한 둠 */
const MAX_CROP_EXPORT_SIDE = 4096

const isNonEmptyImageDataUrl = (dataUrl: string): boolean => {
  if (!dataUrl.startsWith('data:image')) return false
  const comma = dataUrl.indexOf(',')
  if (comma < 0) return false
  return dataUrl.slice(comma + 1).trim().length > 0
}

interface CroppedAreaPixels {
  x: number
  y: number
  width: number
  height: number
}

export interface ImageCropResult {
  /** 결과 이미지의 data URL (jpeg 우선, 실패 시 png) */
  dataUrl: string
  /** 결과 mime 타입 */
  mime: string
  /** 결과 File 객체 */
  file: File
}

interface ImageCropModalProps {
  /** 모달 열림 상태 */
  isOpen: boolean
  /** 원본 이미지 data URL */
  imageDataUrl: string | null
  /** 가로:세로 비율. 1이면 정사각형 (기본 1) */
  aspect?: number
  /** 결과 파일명 (기본 cropped.jpg) */
  fileName?: string
  /** 상단 제목 (기본 "사진 편집") */
  title?: string
  /** 사용자가 완료를 누르면 호출. result.file을 업로드에 그대로 사용 가능 */
  onConfirm: (result: ImageCropResult) => void
  /** 사용자가 취소를 누르면 호출 */
  onCancel: () => void
}

/**
 * 어떤 곳에서든 이미지 업로드 후 크롭/줌/이동이 필요한 경우 사용.
 *
 * 사용 예:
 *   <ImageCropModal
 *     isOpen={cropOpen}
 *     imageDataUrl={originalDataUrl}
 *     aspect={1}
 *     onConfirm={(r) => { setPreview(r.dataUrl); setFile(r.file); setCropOpen(false) }}
 *     onCancel={() => setCropOpen(false)}
 *   />
 */
const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  imageDataUrl,
  aspect = 1,
  fileName = 'cropped.jpg',
  title = '사진 편집',
  onConfirm,
  onCancel,
}) => {
  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState<CroppedAreaPixels | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const onCropComplete = useCallback((px: CroppedAreaPixels) => {
    setCroppedAreaPixels(px)
  }, [])

  const handleConfirm = async () => {
    if (!imageDataUrl) return
    if (!croppedAreaPixels || !canvasRef.current) return
    if (isProcessing) return
    setIsProcessing(true)

    try {
      const image = new Image()
      image.crossOrigin = 'anonymous'
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve()
        image.onerror = () => reject(new Error('Image load failed'))
        image.src = imageDataUrl
      })

      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        setIsProcessing(false)
        return
      }

      const { x, y, width, height } = croppedAreaPixels
      let outW = Math.max(1, Math.round(width))
      let outH = Math.max(1, Math.round(height))
      if (outW > MAX_CROP_EXPORT_SIDE || outH > MAX_CROP_EXPORT_SIDE) {
        const r = Math.min(
          MAX_CROP_EXPORT_SIDE / outW,
          MAX_CROP_EXPORT_SIDE / outH
        )
        outW = Math.max(1, Math.floor(outW * r))
        outH = Math.max(1, Math.floor(outH * r))
      }

      canvas.width = outW
      canvas.height = outH
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(image, x, y, width, height, 0, 0, outW, outH)

      let base64 = canvas.toDataURL('image/jpeg', 0.92)
      let mime = 'image/jpeg'
      if (!isNonEmptyImageDataUrl(base64)) {
        base64 = canvas.toDataURL('image/png')
        mime = 'image/png'
      }
      if (!isNonEmptyImageDataUrl(base64)) {
        devDebug.log('[ImageCropModal] export failed', {
          canvasOut: { w: outW, h: outH },
        })
        setIsProcessing(false)
        return
      }

      // base64 → File 변환
      const arr = base64.split(',')
      const byteString = atob(arr[1])
      const buf = new Uint8Array(byteString.length)
      for (let i = 0; i < byteString.length; i++) {
        buf[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([buf], { type: mime })
      const finalName = mime === 'image/png'
        ? fileName.replace(/\.(jpg|jpeg)$/i, '.png')
        : fileName
      const file = new File([blob], finalName, { type: mime })

      // 상태 초기화 후 결과 콜백
      setCroppedAreaPixels(null)
      setIsProcessing(false)
      onConfirm({ dataUrl: base64, mime, file })
    } catch (error) {
      devDebug.log('[ImageCropModal] confirm error', { error: String(error) })
      setIsProcessing(false)
    }
  }

  const handleCancel = () => {
    if (isProcessing) return
    setCroppedAreaPixels(null)
    onCancel()
  }

  if (!isOpen || !imageDataUrl) return null

  return (
    <>
      {/* 오버레이 */}
      <div className="fixed inset-0 bg-black z-[1060]" />

      {/* 모달 본문 (풀스크린) — 크롭 드래그 중 스와이프 뒤로가기 방지.
          fixed라 .app-swipe-root의 immersive 상단 패딩을 못 받으므로,
          여기서 직접 안전영역만큼 밀어 헤더(뒤로가기·완료)가 노치 뒤로 숨지 않게 한다. */}
      <div
        className="fixed inset-0 z-[1060] flex flex-col bg-backgroundColorBg2"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        data-swipe-back-ignore
      >
        {/* 헤더 — 다른 화면과 동일한 AppBar 사용 (뒤로가기 화살표 + 가운데 제목 + 우측 완료) */}
        <AppBar
          title={title}
          isBottomBorder
          onBack={handleCancel}
          rightElement={
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isProcessing || !croppedAreaPixels}
              className="font-pretendard font-bold text-16px leading-24px text-[#6B5E46] pr-20px disabled:opacity-50"
            >
              완료
            </button>
          }
        />

        {/* 크롭 영역 */}
        <div
          className="w-full relative overflow-hidden"
          style={{
            aspectRatio: `${aspect}`,
            maxHeight: '70vh',
            background: 'var(--backgroundColorBg2, #F5F0E8)',
          }}
        >
          <InstagramCropper
            image={imageDataUrl}
            aspect={aspect}
            onCropComplete={onCropComplete}
          />
        </div>

        {/* 숨겨진 canvas */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </>
  )
}

export default ImageCropModal
